import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { showcaseGridClass, showcaseCardClass } from "../lib/showcaseLayout";
import WebsiteBanner from "../components/WebsiteBanner";
import { PromoBanner } from "../components/PromoBanner";
import { useSettings } from "../context/SettingsContext";
import { Seo } from "../components/Seo";
import { ScentAlchemist } from "../components/ScentAlchemist";
import { MemberClub } from "../components/MemberClub";
import { ArrowRight, Droplet, FlaskConical, Ruler, Sparkles, Feather, Gem, Leaf, Flame, Star, Heart, Sun, Moon, Crown, Wand2, Palette, Clock, Gift, Award, Layers, Brush, Wind, Atom, Beaker } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1622618991746-fe6004db3a47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const RITUAL_ICONS = { droplet: Droplet, flask: FlaskConical, beaker: Beaker, ruler: Ruler, sparkles: Sparkles, feather: Feather, gem: Gem, leaf: Leaf, flame: Flame, star: Star, heart: Heart, sun: Sun, moon: Moon, crown: Crown, wand: Wand2, palette: Palette, clock: Clock, gift: Gift, award: Award, layers: Layers, brush: Brush, wind: Wind, atom: Atom };
const FALLBACK_STEP_ICONS = [Droplet, FlaskConical, Ruler, Sparkles];
const resolveIcon = (name, i) => RITUAL_ICONS[(name || "").toLowerCase()] || FALLBACK_STEP_ICONS[i % FALLBACK_STEP_ICONS.length];

const isExternal = (url) => /^https?:\/\//i.test(url || "");

const ShowcaseCard = ({ c, count }) => {
  const cls = `group relative overflow-hidden lux-card block reveal ${showcaseCardClass(c, count)}`;
  const inner = (
    <>
      <img src={fileUrl(c.image_url)} alt={c.alt || c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 p-5 sm:p-6">
        <h3 className="font-serif-display text-xl sm:text-2xl text-[#F4EFE6] group-hover:text-[#C5A059] transition-colors">{c.title}</h3>
        {c.subtitle && <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-sm line-clamp-2">{c.subtitle}</p>}
        <span className="font-mono-label text-[9px] uppercase tracking-[0.2em] text-[#C5A059] flex items-center gap-1 mt-2">Explore <ArrowRight className="w-3 h-3" /></span>
      </div>
    </>
  );
  if (isExternal(c.link)) {
    return <a href={c.link} target="_blank" rel="noopener noreferrer" data-testid={`showcase-card-${c.id}`} className={cls}>{inner}</a>;
  }
  return <Link to={c.link || "/shop"} data-testid={`showcase-card-${c.id}`} className={cls}>{inner}</Link>;
};

export default function Home() {
  const settings = useSettings();
  const h = settings.homepage || {};
  const seo = settings.seo || {};
  const [concs, setConcs] = useState([]);
  const [catalog, setCatalog] = useState({ concentrations: [], bottle_sizes: [], showcase: [] });

  useEffect(() => {
    api.get("/catalog").then((r) => { setConcs(r.data.concentrations || []); setCatalog(r.data); }).catch(() => {});
  }, []);

  const steps = ((h.ritual_steps && h.ritual_steps.length ? h.ritual_steps : [
    { t: "Choose Your Fragrance", d: "Twenty-one hand-crafted compositions, from modern icons to heritage attars." },
    { t: "Choose Your Expression", d: "EDP, Parfum or Attar — pick the concentration that suits your intensity." },
    { t: "Choose Your Bottle Size", d: "From a 2 ML discovery to a 100 ML statement — you decide the scale." },
    { t: "Create Your Signature Blend", d: "Or fuse two fragrances into a single duet that belongs to you alone." },
  ])).map((s, i) => ({ ...s, Icon: resolveIcon(s.icon, i) }));

  const packagingStats = (h.packaging_stats && h.packaging_stats.length ? h.packaging_stats : [
    { k: "100%", l: "Into the Juice" }, { k: "0%", l: "Wasted on Gimmicks" },
    { k: "Fresh", l: "Made to Order" }, { k: "Pure", l: "Ingredients" },
  ]);

  const exprChips = (h.brand_structure_expressions || "EDP | Parfum | Attar").split("|").map((x) => x.trim()).filter(Boolean);

  return (
    <div data-testid="home-page">
      <Seo title={seo.title || "HYPPE AROMA — Enhance Your Natural Aura"} description={seo.description || "Luxury customizable fragrances — EDP, Parfum & Attar. Hand-blended in Ahmedabad."} image={seo.og_image} path="/" />

      <PromoBanner />
      <WebsiteBanner placement="homepage" />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={h.hero_image || HERO_IMG} alt="HYPPE AROMA — luxury fragrance atelier" fetchPriority="high" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/85 to-[#2D0A11]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-12 pt-24 pb-28 sm:pt-32 sm:pb-40">
          <div className="max-w-3xl reveal">
            <p className="eyebrow mb-8">{h.hero_eyebrow || "◐ Digital-First Luxury Fragrance House · Est. 2026"}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#F4EFE6]">
              {h.hero_title_line1 || "Enhance Your"}<br />
              <span className="italic text-[#C5A059]">{h.hero_title_highlight || "Natural Aura."}</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70">
              {h.hero_subtitle || "Premium, made-to-order fragrances — freshly hand-blended in our Ahmedabad atelier and customizable down to the last note."}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/shop" data-testid="hero-shop-cta" className="btn-gold">Explore The Library</Link>
              <a href="#alchemist" data-testid="hero-alchemist-cta" className="btn-outline-gold">Compose a Duet <ArrowRight className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STRUCTURE */}
      <section className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-5 sm:px-12 py-16 text-center">
          <p className="font-serif-display text-3xl sm:text-4xl text-[#F4EFE6] leading-snug">{h.brand_structure_line1 || "One Fragrance."}</p>
          <p className="font-serif-display text-3xl sm:text-4xl italic text-[#C5A059] leading-snug mt-1">{h.brand_structure_line2 || "Three Expressions."}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono-label text-[11px] uppercase tracking-[0.3em] text-white/60">
            {exprChips.map((c, i) => (
              <span key={i} className="flex items-center gap-x-8">
                {i > 0 && <span className="text-[#C5A059]">·</span>}
                <span>{c}</span>
              </span>
            ))}
          </div>
          <p className="mt-8 font-serif-display text-2xl sm:text-3xl text-[#F4EFE6]/90">{h.brand_structure || "Infinite Possibilities."}</p>
        </div>
      </section>

      {/* CREATE YOUR OWN */}
      <section data-testid="create-section" className="max-w-7xl mx-auto px-5 sm:px-12 py-24">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">{h.ritual_eyebrow || "◐ The Ritual"}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F4EFE6] leading-tight">{h.ritual_title || "Create Your Own Fragrance."}</h2>
          <p className="mt-5 text-muted-ivory leading-relaxed">{h.ritual_body || "Luxury is personal. In four considered steps, compose a scent that is unmistakably, entirely yours."}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {steps.map((s, i) => {
            const Icon = s.Icon;
            return (
              <div key={`${s.t}-${i}`} className="bg-[#0A0A0A] p-8 group hover:bg-[#120306] transition-colors">
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.4} />
                  <span className="font-serif-display text-4xl text-white/10 group-hover:text-[#C5A059]/30 transition-colors">0{i + 1}</span>
                </div>
                <h3 className="font-serif-display text-xl text-[#F4EFE6] leading-snug">{s.t}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-10"><Link to="/shop" className="btn-outline-gold">Start Creating <ArrowRight className="w-4 h-4" /></Link></div>
      </section>

      {/* CHOOSE YOUR EXPRESSION */}
      <section data-testid="expression-section" className="maroon-panel grain border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-12 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-4">{h.expression_eyebrow || "◐ Choose Your Expression"}</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F4EFE6]">{h.expression_title || "The same soul, three intensities."}</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {(concs.length ? concs : []).map((c) => (
              <div key={c.key} data-testid={`expression-card-${c.key}`} className="lux-card p-8 text-center hover:-translate-y-1">
                <p className="font-serif-display text-6xl text-[#C5A059]">{c.oil_pct}%</p>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">Perfume Oil</p>
                <div className="gold-hairline my-6" />
                <h3 className="font-serif-display text-2xl text-[#F4EFE6]">{c.label}</h3>
                <p className="mt-2 font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
                  {c.alcohol_pct > 0 ? `${c.alcohol_pct}% Alcohol` : "Alcohol-Free"}
                </p>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">{c.display_text}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-center max-w-2xl mx-auto font-serif-display text-lg sm:text-xl italic text-[#F4EFE6]/80 leading-relaxed">
            “{h.expression_note || "We intentionally do not offer EDT or EDC because we believe premium fragrances deserve premium concentrations."}”
          </p>
        </div>
      </section>

      {/* SCENT ALCHEMIST / DUET */}
      <ScentAlchemist />

      {/* HOMEPAGE SHOWCASE (dynamic — managed in Admin → Showcase) */}
      {(catalog.showcase || []).length > 0 && (
        <section data-testid="showcase-section" className="border-y border-white/10 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-5 sm:px-12 py-24">
            <div className="max-w-2xl mb-12">
              <p className="eyebrow mb-4">{h.showcase_eyebrow || "◐ Discover"}</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F4EFE6] leading-tight">{h.showcase_title || "Find your olfactive world."}</h2>
              <p className="mt-5 text-muted-ivory leading-relaxed">{h.showcase_body || "From fresh citrus to deep oud — explore the library by the scent world you love most."}</p>
            </div>
            <div className={showcaseGridClass(catalog.showcase.length)}>
              {catalog.showcase.map((c) => <ShowcaseCard key={c.id} c={c} count={catalog.showcase.length} />)}
            </div>
          </div>
        </section>
      )}

      {/* BRAND PHILOSOPHY */}
      <section data-testid="philosophy-section" className="border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-12 py-28 text-center">
          <p className="eyebrow mb-6">{h.philosophy_eyebrow || "◐ Brand Philosophy"}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] leading-[1.05]">
            {h.philosophy_title || "Fragrance is the pathway to nostalgia."}
          </h2>
          <p className="mt-10 text-lg text-white/70 leading-loose max-w-2xl mx-auto">
            {h.philosophy_body || "Kruparth believes a fragrance should not only smell beautiful — it should awaken memories, emotions and moments that stay with you forever."}
          </p>
        </div>
      </section>

      {/* PACKAGING PHILOSOPHY */}
      <section data-testid="packaging-section" className="max-w-7xl mx-auto px-5 sm:px-12 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="eyebrow mb-6">{h.packaging_eyebrow || "◐ Packaging Philosophy"}</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#F4EFE6] leading-tight">
              {h.packaging_title || "We invest in the fragrance, not the box."}
            </h2>
            <p className="mt-8 text-muted-ivory leading-loose max-w-xl">
              {h.packaging_body || "We invest in the fragrance rather than unnecessary packaging. You're paying for exceptional ingredients, craftsmanship and personalization — not expensive packaging gimmicks."}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {packagingStats.map((st) => (
                <div key={st.l} className="bg-[#0A0A0A] p-7 text-center">
                  <p className="font-serif-display text-3xl text-[#C5A059]">{st.k}</p>
                  <p className="font-mono-label text-[9px] uppercase tracking-[0.2em] text-white/50 mt-2">{st.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MemberClub />
    </div>
  );
}
